import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Upload, 
  Layers, 
  Database, 
  LogOut, 
  Home,
  Shield,
  Info,
  BarChart3
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    verifyToken();
  }, []);

  const verifyToken = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }

    try {
      const res = await fetch(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        setIsVerified(true);
      } else if (res.status === 401) {
        // Only remove token if server explicitly says it's invalid
        localStorage.removeItem("adminToken");
        navigate("/admin/login");
      } else {
        // For other errors, assume token is still valid
        setIsVerified(true);
      }
    } catch (err) {
      // Network error - don't remove token, assume still valid
      console.warn("Token verification failed due to network error, assuming valid");
      setIsVerified(true);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem("adminToken");
    try {
      await fetch(`${API}/admin/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      // Ignore errors on logout
    }
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <div className="admin-dashboard-page">
        <div className="loading-container">
          <p>Verifying authentication...</p>
        </div>
      </div>
    );
  }

  if (!isVerified) {
    return null;
  }

  return (
    <div className="admin-dashboard-page" data-testid="admin-dashboard-page">
      <header className="admin-header">
        <div className="admin-header-left">
          <Shield size={24} className="admin-shield" />
          <h1>Admin Dashboard</h1>
        </div>
        <div className="admin-header-right">
          <Link to="/" className="header-link">
            <Home size={18} />
            View Site
          </Link>
          <Button variant="outline" onClick={handleLogout} className="logout-btn" data-testid="logout-btn">
            <LogOut size={18} />
            Logout
          </Button>
        </div>
      </header>

      <main className="admin-main">
        <div className="admin-grid">
          <Link to="/admin/upload" className="admin-card" data-testid="upload-card">
            <div className="card-icon upload-icon">
              <Upload size={32} />
            </div>
            <h2>Upload Chart</h2>
            <p>Upload a single chart image for a specific date and hour</p>
          </Link>

          <Link to="/admin/bulk-upload" className="admin-card" data-testid="bulk-upload-card">
            <div className="card-icon bulk-icon">
              <Layers size={32} />
            </div>
            <h2>Bulk Upload</h2>
            <p>Upload up to 24 chart images for a full day (00:00 - 23:59)</p>
          </Link>

          <Link to="/admin/charts" className="admin-card" data-testid="manage-charts-card">
            <div className="card-icon manage-icon">
              <Database size={32} />
            </div>
            <h2>Manage Charts</h2>
            <p>View, search, and delete uploaded chart data</p>
          </Link>

          <Link to="/admin/info-upload" className="admin-card" data-testid="info-upload-card">
            <div className="card-icon info-icon">
              <Info size={32} />
            </div>
            <h2>Important Info</h2>
            <p>Upload info images for a specific date and hour</p>
          </Link>

          <Link to="/admin/category-upload" className="admin-card" data-testid="category-upload-card">
            <div className="card-icon category-icon">
              <BarChart3 size={32} />
            </div>
            <h2>Category Forecasts</h2>
            <p>Upload forecast images (Business, Wealth, Career, etc.)</p>
          </Link>
        </div>
      </main>

      <footer className="admin-footer">
        <p className="footer-copyright">
          Copyright © 2026 Steven Yong - Digital Dive Coaching Sdn. Bhd. All rights reserved worldwide.
        </p>
      </footer>
    </div>
  );
}
